// src/module_pharmacy/AddTransaction.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faSave, faTimes, faTruck, faUsers, faUserTie, faBox, faEnvelope, faCalculator, faUniversity, faPercent, faEllipsisH,
    faWallet, faDollarSign, faCalendarAlt, faCreditCard,
    faFileInvoice, faStickyNote, faReceipt, faLayerGroup,
    faChevronDown, faExchangeAlt, faCheckCircle, faExclamationTriangle,
    faSpinner, faTag, faStore, faSearch, faUser, faBuilding,faUserPlus,faPlane,faHotel,faHome,faLanguage,faCode,faScissors,faIdCard,
    faList, faFileAlt, faMoneyBillWave, faClock, faArrowRight,
    faArrowLeft, faInfoCircle, faCheckDouble, faBalanceScale,
    faCheck, faBan, faExclamationCircle, faCircle, faCheckCircle as faCheckCircleSolid,
    faChartLine, faCoins, faShoppingCart, faUtensils, faCoffee,
    faTools, faWrench, faLightbulb, faBolt, faFire, faWater,
    faSnowflake, faCog, faShieldAlt, faClipboardList, faBoxes,
    faHands, faChair, faCouch, faTshirt, faPaintBrush,
    faBroom, faTrashAlt, faPhone, faWifi, faTv, faDesktop,
    faPrint, faCar, faGasPump, faLeaf, faSeedling, faTree,
    faMountain, faUmbrella, faSun, faMoon, faCloud, faCloudRain,
    faSnowman, faWind, faGlobe, faFlag, faHeart, faStar,
    faGift, faGem, faCrown, faMedal, faTrophy, faMusic,
    faFilm, faCamera, faVideo, faBook, faGraduationCap,
    faSchool, faHospital, faAmbulance, faHeartbeat, faPills,
    faSyringe, faBandAid, faTooth, faEye, faEar, faHands as faHandsHelping,
    faAppleAlt, faCarrot, faBread, faCheese, faEgg,
    faFish, faDrumstick, faCookie, faCandy, faIceCream,
    faShoppingBag, faGavel, faFileContract, faPenFancy, faPencilAlt,
    faRuler, faWeight, faScaleBalanced, faClock as faClockRegular,
    faHourglass, faStopwatch, faCalendar, faCalendarDay, faCalendarWeek,
    faCalendarAlt as faCalendarAlt2, faClockFour, faHourglassHalf,
    faStopwatch as faStopwatch2, faTimer, faAlarmClock, faBell,
    faBullhorn, faMegaphone, faMicrophone, faHeadphones, faMusic as faMusicNote,
    faDrum, faGuitar, faPiano, faTheaterMasks, faClapperboard,
    faTicket, faMask, faPalette, faBrush, faPencil,
    faRulerCombined, faRulerHorizontal, faRulerVertical,
    faSort, faFilter, faSlidersH, faAdjust, faPalette as faPalette2,
    faSwatchbook, faDroplet, faTint, faFlask, faVial,
    faMagnet, faAtom, faMicroscope, faDNA, faFlask as faFlask2,
    faMicrochip, faMemory, faServer, faDatabase, faCloudUploadAlt,
    faCloudDownloadAlt, faCloudUpload, faCloudDownload, faSyncAlt,
    faUndoAlt, faRedoAlt, faHistory, faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import "../module_mechanics/Styles/Trans.css";

// ==============================================
// API ENDPOINTS
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/mechanics_module';
const TRANSACTIONS_API = `${API_BASE}/cosmetics_transactions.php`;
const CUSTOMERS_API = `${API_BASE}/mechanics_customers.php`;
const SUPPLIERS_API = `${API_BASE}/mechanics_suppliers.php`;
const EMPLOYEES_API = `${API_BASE}/mechanics_employees.php`;

// ==============================================
// HELPER FUNCTIONS
// ==============================================
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// ==============================================
// COMPREHENSIVE EXPENSE CATEGORIES WITH ICONS
// ==============================================
const EXPENSE_CATEGORIES = [
    // 🏪 Business Operations
    { value: 'rent', label: 'Rent / Lease', icon: faBuilding, group: 'Business Operations' },
    { value: 'utilities', label: 'Utilities (Electricity, Water, Gas)', icon: faBolt, group: 'Business Operations' },
    { value: 'internet', label: 'Internet & Phone', icon: faWifi, group: 'Business Operations' },
    { value: 'maintenance', label: 'Maintenance & Repairs', icon: faTools, group: 'Business Operations' },
    { value: 'cleaning', label: 'Cleaning & Sanitation', icon: faBroom, group: 'Business Operations' },
    { value: 'security', label: 'Security Services', icon: faShieldAlt, group: 'Business Operations' },
    { value: 'insurance', label: 'Business Insurance', icon: faShieldAlt, group: 'Business Operations' },
    { value: 'licenses', label: 'Licenses & Permits', icon: faFileContract, group: 'Business Operations' },
    
    // 🍽️ Food & Dining
    { value: 'eating_out', label: 'Eating Out / Restaurants', icon: faUtensils, group: 'Food & Dining' },
    { value: 'groceries', label: 'Groceries / Supplies', icon: faShoppingCart, group: 'Food & Dining' },
    { value: 'coffee_break', label: 'Coffee / Tea Break', icon: faCoffee, group: 'Food & Dining' },
    { value: 'staff_meals', label: 'Staff Meals / Catering', icon: faUsers, group: 'Food & Dining' },
    { value: 'serving', label: 'Serving / Hospitality', icon: faHands, group: 'Food & Dining' },
    { value: 'guest_treats', label: 'Guest Treats / Entertainment', icon: faGift, group: 'Food & Dining' },
    { value: 'kitchen_supplies', label: 'Kitchen Supplies', icon: faUtensils, group: 'Food & Dining' },
    { value: 'dining_equipment', label: 'Dining Equipment', icon: faChair, group: 'Food & Dining' },
    
    // 👥 Staff & Employees
    { value: 'salary', label: 'Salary / Wages', icon: faMoneyBillWave, group: 'Staff & Employees' },
    { value: 'bonus', label: 'Bonuses & Incentives', icon: faTrophy, group: 'Staff & Employees' },
    { value: 'allowances', label: 'Allowances (Travel, Fuel)', icon: faCar, group: 'Staff & Employees' },
    { value: 'training', label: 'Training & Development', icon: faGraduationCap, group: 'Staff & Employees' },
    { value: 'uniforms', label: 'Uniforms / Workwear', icon: faTshirt, group: 'Staff & Employees' },
    { value: 'medical', label: 'Medical / Health Insurance', icon: faHospital, group: 'Staff & Employees' },
    { value: 'recruitment', label: 'Recruitment & Hiring', icon: faUserPlus, group: 'Staff & Employees' },
    { value: 'employee_benefits', label: 'Employee Benefits', icon: faHeart, group: 'Staff & Employees' },
    
    // 🔧 Repair & Maintenance
    { value: 'repair', label: 'General Repairs', icon: faWrench, group: 'Repair & Maintenance' },
    { value: 'equipment_repair', label: 'Equipment Repair', icon: faCog, group: 'Repair & Maintenance' },
    { value: 'vehicle_repair', label: 'Vehicle Repair', icon: faCar, group: 'Repair & Maintenance' },
    { value: 'furniture_repair', label: 'Furniture Repair', icon: faChair, group: 'Repair & Maintenance' },
    { value: 'building_repair', label: 'Building Repair', icon: faBuilding, group: 'Repair & Maintenance' },
    { value: 'plumbing', label: 'Plumbing Services', icon: faWater, group: 'Repair & Maintenance' },
    { value: 'electrical', label: 'Electrical Services', icon: faLightbulb, group: 'Repair & Maintenance' },
    { value: 'hvac', label: 'HVAC Services', icon: faSnowflake, group: 'Repair & Maintenance' },
    
    // 🛒 Inventory & Stock
    { value: 'inventory', label: 'Inventory Purchase', icon: faBoxes, group: 'Inventory & Stock' },
    { value: 'raw_materials', label: 'Raw Materials', icon: faLayerGroup, group: 'Inventory & Stock' },
    { value: 'packaging', label: 'Packaging Materials', icon: faBox, group: 'Inventory & Stock' },
    { value: 'supplies', label: 'Office Supplies', icon: faClipboardList, group: 'Inventory & Stock' },
    { value: 'stationery', label: 'Stationery', icon: faFileAlt, group: 'Inventory & Stock' },
    { value: 'warehouse', label: 'Warehouse Storage', icon: faStore, group: 'Inventory & Stock' },
    { value: 'inventory_management', label: 'Inventory Management', icon: faList, group: 'Inventory & Stock' },
    
    // 💡 Utilities & Services
    { value: 'electricity', label: 'Electricity Bills', icon: faLightbulb, group: 'Utilities & Services' },
    { value: 'water', label: 'Water Bills', icon: faWater, group: 'Utilities & Services' },
    { value: 'gas', label: 'Gas / Fuel', icon: faFire, group: 'Utilities & Services' },
    { value: 'heating', label: 'Heating / AC', icon: faSnowflake, group: 'Utilities & Services' },
    { value: 'sewage', label: 'Sewage / Waste', icon: faTrashAlt, group: 'Utilities & Services' },
    { value: 'garbage', label: 'Garbage Disposal', icon: faTrashAlt, group: 'Utilities & Services' },
    { value: 'recycling', label: 'Recycling Services', icon: faLeaf, group: 'Utilities & Services' },
    
    // 📱 Communication
    { value: 'phone_bills', label: 'Phone Bills', icon: faPhone, group: 'Communication' },
    { value: 'internet_bill', label: 'Internet Bills', icon: faWifi, group: 'Communication' },
    { value: 'postage', label: 'Postage & Courier', icon: faEnvelope, group: 'Communication' },
    { value: 'broadcasting', label: 'Broadcasting', icon: faBullhorn, group: 'Communication' },
    { value: 'newsletter', label: 'Newsletter / Publications', icon: faFileAlt, group: 'Communication' },
    
    // 🖥️ Technology
    { value: 'software', label: 'Software Subscriptions', icon: faDesktop, group: 'Technology' },
    { value: 'hardware', label: 'Hardware / Equipment', icon: faPrint, group: 'Technology' },
    { value: 'website', label: 'Website Hosting', icon: faGlobe, group: 'Technology' },
    { value: 'it_services', label: 'IT Services', icon: faShieldAlt, group: 'Technology' },
    { value: 'cloud_services', label: 'Cloud Services', icon: faCloud, group: 'Technology' },
    { value: 'cybersecurity', label: 'Cybersecurity', icon: faShieldAlt, group: 'Technology' },
    { value: 'tech_support', label: 'Tech Support', icon: faCog, group: 'Technology' },
    
    // 🚗 Transportation
    { value: 'fuel', label: 'Fuel / Petrol', icon: faGasPump, group: 'Transportation' },
    { value: 'tolls', label: 'Tolls & Parking', icon: faMoneyBillWave, group: 'Transportation' },
    { value: 'public_transport', label: 'Public Transport', icon: faTruck, group: 'Transportation' },
    { value: 'delivery', label: 'Delivery Services', icon: faTruck, group: 'Transportation' },
    { value: 'vehicle_maintenance', label: 'Vehicle Maintenance', icon: faCar, group: 'Transportation' },
    { value: 'car_rental', label: 'Car Rental', icon: faCar, group: 'Transportation' },
    { value: 'flights', label: 'Flights / Air Travel', icon: faPlane, group: 'Transportation' },
    { value: 'hotels', label: 'Hotels / Accommodation', icon: faHotel, group: 'Transportation' },
    
    // 🏢 Property
    { value: 'property_tax', label: 'Property Tax', icon: faBuilding, group: 'Property' },
    { value: 'property_insurance', label: 'Property Insurance', icon: faShieldAlt, group: 'Property' },
    { value: 'landscaping', label: 'Landscaping', icon: faTree, group: 'Property' },
    { value: 'pest_control', label: 'Pest Control', icon: faBan, group: 'Property' },
    { value: 'mortgage', label: 'Mortgage Payments', icon: faHome, group: 'Property' },
    { value: 'property_management', label: 'Property Management', icon: faBuilding, group: 'Property' },
    
    // 🎉 Events & Entertainment
    { value: 'events', label: 'Events / Functions', icon: faStar, group: 'Events & Entertainment' },
    { value: 'entertainment', label: 'Entertainment', icon: faFilm, group: 'Events & Entertainment' },
    { value: 'decorations', label: 'Decorations', icon: faPaintBrush, group: 'Events & Entertainment' },
    { value: 'photography', label: 'Photography', icon: faCamera, group: 'Events & Entertainment' },
    { value: 'music', label: 'Music / DJ', icon: faMusic, group: 'Events & Entertainment' },
    { value: 'catering', label: 'Catering Services', icon: faUtensils, group: 'Events & Entertainment' },
    { value: 'venue_rental', label: 'Venue Rental', icon: faBuilding, group: 'Events & Entertainment' },
    { value: 'party_supplies', label: 'Party Supplies', icon: faGift, group: 'Events & Entertainment' },
    
    // 📋 Professional Services
    { value: 'legal', label: 'Legal Fees', icon: faBalanceScale, group: 'Professional Services' },
    { value: 'accounting', label: 'Accounting / Audit', icon: faCalculator, group: 'Professional Services' },
    { value: 'consulting', label: 'Consulting Services', icon: faUserTie, group: 'Professional Services' },
    { value: 'marketing', label: 'Marketing & Advertising', icon: faChartLine, group: 'Professional Services' },
    { value: 'pr_services', label: 'PR Services', icon: faBullhorn, group: 'Professional Services' },
    { value: 'translation', label: 'Translation Services', icon: faLanguage, group: 'Professional Services' },
    { value: 'graphic_design', label: 'Graphic Design', icon: faPalette, group: 'Professional Services' },
    { value: 'web_development', label: 'Web Development', icon: faCode, group: 'Professional Services' },
    
    // 🏥 Health & Safety
    { value: 'safety', label: 'Safety Equipment', icon: faShieldAlt, group: 'Health & Safety' },
    { value: 'first_aid', label: 'First Aid Supplies', icon: faBandAid, group: 'Health & Safety' },
    { value: 'sanitation', label: 'Sanitation Products', icon: faHandsHelping, group: 'Health & Safety' },
    { value: 'ppe', label: 'PPE Equipment', icon: faShieldAlt, group: 'Health & Safety' },
    { value: 'medical_supplies', label: 'Medical Supplies', icon: faPills, group: 'Health & Safety' },
    { value: 'health_screenings', label: 'Health Screenings', icon: faHeartbeat, group: 'Health & Safety' },
    { value: 'wellness_programs', label: 'Wellness Programs', icon: faLeaf, group: 'Health & Safety' },
    
    // 🌿 Environment
    { value: 'recycling_service', label: 'Recycling / Waste', icon: faLeaf, group: 'Environment' },
    { value: 'green_energy', label: 'Green Energy', icon: faSeedling, group: 'Environment' },
    { value: 'environmental', label: 'Environmental Fees', icon: faTree, group: 'Environment' },
    { value: 'sustainability', label: 'Sustainability Programs', icon: faGlobe, group: 'Environment' },
    { value: 'carbon_offset', label: 'Carbon Offset', icon: faCloud, group: 'Environment' },
    
    // 🎓 Education
    { value: 'education', label: 'Education / Courses', icon: faGraduationCap, group: 'Education' },
    { value: 'books', label: 'Books & Materials', icon: faBook, group: 'Education' },
    { value: 'workshops', label: 'Workshops / Seminars', icon: faSchool, group: 'Education' },
    { value: 'tuition', label: 'Tuition Fees', icon: faUniversity, group: 'Education' },
    { value: 'certifications', label: 'Certifications', icon: faMedal, group: 'Education' },
    
    // 💰 Financial
    { value: 'bank_fees', label: 'Bank Fees', icon: faUniversity, group: 'Financial' },
    { value: 'interest', label: 'Interest Payments', icon: faPercent, group: 'Financial' },
    { value: 'tax', label: 'Tax Payments', icon: faBalanceScale, group: 'Financial' },
    { value: 'investment', label: 'Investments', icon: faChartLine, group: 'Financial' },
    { value: 'loan_payment', label: 'Loan Payments', icon: faMoneyBillWave, group: 'Financial' },
    { value: 'dividends', label: 'Dividends', icon: faCoins, group: 'Financial' },
    { value: 'currency_exchange', label: 'Currency Exchange', icon: faExchangeAlt, group: 'Financial' },
    
    // 🎨 Creative & Design
    { value: 'art_supplies', label: 'Art Supplies', icon: faPaintBrush, group: 'Creative & Design' },
    { value: 'design_software', label: 'Design Software', icon: faPenFancy, group: 'Creative & Design' },
    { value: 'printing', label: 'Printing Services', icon: faPrint, group: 'Creative & Design' },
    { value: 'fabric', label: 'Fabric / Materials', icon: faTshirt, group: 'Creative & Design' },
    { value: 'craft_supplies', label: 'Craft Supplies', icon: faScissors, group: 'Creative & Design' },
    
    // 🏠 General
    { value: 'general', label: 'General / Other', icon: faList, group: 'General' },
    { value: 'miscellaneous', label: 'Miscellaneous', icon: faEllipsisH, group: 'General' },
    { value: 'petty_cash', label: 'Petty Cash', icon: faCoins, group: 'General' },
    { value: 'donations', label: 'Donations / Charitable', icon: faHeart, group: 'General' },
    { value: 'membership', label: 'Membership Fees', icon: faIdCard, group: 'General' },
    { value: 'subscription', label: 'Subscriptions', icon: faCalendarAlt, group: 'General' }
];

// ==============================================
// TRANSLATION SYSTEM
// ==============================================
const translations = {
    'en': {
        addTransaction: 'Add Transaction',
        addNewTransaction: 'Record a new financial transaction',
        customerDue: 'Customer Due',
        customerPayment: 'Customer',
        supplierDue: 'Supplier Due',
        supplierPayment: 'Supplier',
        salaryPayment: 'Salary',
        shopExpense: 'Expense',
        selectCustomer: 'Select Customer',
        selectSupplier: 'Select Supplier',
        selectEmployee: 'Select Employee',
        amount: 'Amount',
        date: 'Date',
        description: 'Description',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        cheque: 'Cheque',
        creditCard: 'Credit Card',
        mobilePayment: 'Mobile Payment',
        invoiceNumber: 'Invoice Number',
        optional: 'Optional',
        save: 'Save',
        clear: 'Clear',
        saving: 'Saving...',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        transactionSaved: 'Transaction saved successfully!',
        failedSave: 'Failed to save transaction',
        selectShopFirst: 'Please select a shop first',
        noShopSelected: 'No shop selected',
        expenseCategory: 'Expense Category',
        expenseName: 'Expense Name',
        unpaidInvoices: 'Unpaid Invoices',
        noUnpaidInvoices: 'No unpaid invoices found',
        grandTotal: 'Grand Total',
        alreadyPaid: 'Already Paid',
        balanceDue: 'Balance Due',
        paymentType: 'Payment Type',
        fullPayment: 'Full Payment',
        partialPayment: 'Partial Payment',
        paymentDetails: 'Payment Details',
        notes: 'Notes',
        selectReference: 'Select Reference',
        expenseDetails: 'Expense Details',
        general: 'General',
        searchPlaceholder: 'Search...',
        noResults: 'No results found',
        select: 'Select',
        invoice: 'Invoice',
        due: 'Due',
        paid: 'Paid',
        total: 'Total',
        customer: 'Customer',
        supplier: 'Supplier',
        employee: 'Employee',
        name: 'Name',
        phone: 'Phone',
        email: 'Email',
        reference: 'Reference',
        selectInvoice: 'Select Invoice',
        paymentAmount: 'Payment Amount',
        totalBalance: 'Total Balance',
        outstandingBalance: 'Outstanding Balance',
        selectInvoices: 'Select Invoices',
        invoiceSelected: 'Invoice Selected',
        pay: 'Pay',
        balance: 'Balance',
        totalDue: 'Total Due',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        remainingBalance: 'Remaining Balance',
        noSupplierSelected: 'No supplier selected',
        selectSupplierFirst: 'Please select a supplier first',
        paymentAmountExceeds: 'Payment amount exceeds selected invoice total',
        invoicesSelected: 'invoices selected',
        totalSelected: 'Total Selected',
        paymentWillBeApplied: 'Payment will be applied to selected invoices using FIFO (oldest first)',
        supplierBalance: 'Supplier Balance',
        loadingInvoices: 'Loading invoices...',
        fifoNote: 'FIFO: Payments are applied to oldest invoices first',
        totalOutstanding: 'Total Outstanding',
        selectInvoicesToPay: 'Select invoices to pay',
        payment: 'Payment',
        pending: 'Pending',
        partiallyPaid: 'Partially Paid',
        totalPaid: 'Total Paid',
        totalPurchases: 'Total Purchases',
        summary: 'Summary',
        selectCategory: 'Select a category...'
    },
    'ur': {
        addTransaction: 'ٹرانزیکشن شامل کریں',
        addNewTransaction: 'نیا مالی ٹرانزیکشن ریکارڈ کریں',
        customerDue: 'کسٹمر بقایا',
        customerPayment: 'کسٹمر ادائیگی',
        supplierDue: 'سپلائر بقایا',
        supplierPayment: 'سپلائر ادائیگی',
        salaryPayment: 'تنخواہ کی ادائیگی',
        shopExpense: 'دکان کا خرچ',
        selectCustomer: 'کسٹمر منتخب کریں',
        selectSupplier: 'سپلائر منتخب کریں',
        selectEmployee: 'ملازم منتخب کریں',
        amount: 'رقم',
        date: 'تاریخ',
        description: 'تفصیل',
        paymentMethod: 'ادائیگی کا طریقہ',
        cash: 'نقد',
        bankTransfer: 'بینک ٹرانسفر',
        cheque: 'چیک',
        creditCard: 'کریڈٹ کارڈ',
        mobilePayment: 'موبائل پیمنٹ',
        invoiceNumber: 'انوائس نمبر',
        optional: 'اختیاری',
        save: 'محفوظ کریں',
        clear: 'صاف کریں',
        saving: 'محفوظ کر رہے ہیں...',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        transactionSaved: 'ٹرانزیکشن کامیابی سے محفوظ ہو گئی!',
        failedSave: 'ٹرانزیکشن محفوظ کرنے میں ناکامی',
        selectShopFirst: 'پہلے ایک شاپ منتخب کریں',
        noShopSelected: 'کوئی شاپ منتخب نہیں',
        expenseCategory: 'خرچ کا زمرہ',
        expenseName: 'خرچ کا نام',
        unpaidInvoices: 'غیر ادا شدہ انوائسز',
        noUnpaidInvoices: 'کوئی غیر ادا شدہ انوائس نہیں',
        grandTotal: 'کل رقم',
        alreadyPaid: 'ادا شدہ رقم',
        balanceDue: 'بقایا رقم',
        paymentType: 'ادائیگی کی قسم',
        fullPayment: 'مکمل ادائیگی',
        partialPayment: 'جزوی ادائیگی',
        paymentDetails: 'ادائیگی کی تفصیلات',
        notes: 'نوٹس',
        selectReference: 'حوالہ منتخب کریں',
        expenseDetails: 'خرچ کی تفصیلات',
        general: 'عام',
        searchPlaceholder: 'تلاش کریں...',
        noResults: 'کوئی نتیجہ نہیں',
        select: 'منتخب کریں',
        invoice: 'انوائس',
        due: 'بقایا',
        paid: 'ادا شدہ',
        total: 'کل',
        customer: 'کسٹمر',
        supplier: 'سپلائر',
        employee: 'ملازم',
        name: 'نام',
        phone: 'فون',
        email: 'ای میل',
        reference: 'حوالہ',
        selectInvoice: 'انوائس منتخب کریں',
        paymentAmount: 'ادائیگی کی رقم',
        totalBalance: 'کل بقایا',
        outstandingBalance: 'بقایا رقم',
        selectInvoices: 'انوائسز منتخب کریں',
        invoiceSelected: 'انوائس منتخب',
        pay: 'ادا کریں',
        balance: 'باقی',
        totalDue: 'کل بقایا',
        selectAll: 'سب منتخب کریں',
        deselectAll: 'سب ہٹائیں',
        remainingBalance: 'باقی رقم',
        noSupplierSelected: 'کوئی سپلائر منتخب نہیں',
        selectSupplierFirst: 'پہلے سپلائر منتخب کریں',
        paymentAmountExceeds: 'ادائیگی کی رقم منتخب انوائسز کی کل سے زیادہ ہے',
        invoicesSelected: 'انوائسز منتخب',
        totalSelected: 'کل منتخب',
        paymentWillBeApplied: 'ادائیگی منتخب انوائسز پر FIFO (پہلے پرانی) کی بنیاد پر لاگو ہوگی',
        supplierBalance: 'سپلائر بیلنس',
        loadingInvoices: 'انوائسز لوڈ ہو رہی ہیں...',
        fifoNote: 'FIFO: ادائیگی پہلے پرانی انوائسز پر لاگو ہوتی ہے',
        totalOutstanding: 'کل بقایا',
        selectInvoicesToPay: 'ادائیگی کے لیے انوائسز منتخب کریں',
        payment: 'ادائیگی',
        pending: 'زیر التواء',
        partiallyPaid: 'جزوی ادائیگی',
        totalPaid: 'کل ادا شدہ',
        totalPurchases: 'کل خریداری',
        summary: 'خلاصہ',
        selectCategory: 'زمرہ منتخب کریں...'
    },
    'ar': {
        addTransaction: 'إضافة معاملة',
        addNewTransaction: 'تسجيل معاملة مالية جديدة',
        customerDue: 'مستحقات العميل',
        customerPayment: 'دفع العميل',
        supplierDue: 'مستحقات المورد',
        supplierPayment: 'دفع للمورد',
        salaryPayment: 'دفع الراتب',
        shopExpense: 'مصروف المحل',
        selectCustomer: 'اختر العميل',
        selectSupplier: 'اختر المورد',
        selectEmployee: 'اختر الموظف',
        amount: 'المبلغ',
        date: 'التاريخ',
        description: 'الوصف',
        paymentMethod: 'طريقة الدفع',
        cash: 'نقد',
        bankTransfer: 'تحويل بنكي',
        cheque: 'شيك',
        creditCard: 'بطاقة ائتمان',
        mobilePayment: 'دفع عبر الجوال',
        invoiceNumber: 'رقم الفاتورة',
        optional: 'اختياري',
        save: 'حفظ',
        clear: 'مسح',
        saving: 'جاري الحفظ...',
        success: 'نجاح',
        error: 'خطأ',
        warning: 'تحذير',
        transactionSaved: 'تم حفظ المعاملة بنجاح!',
        failedSave: 'فشل في حفظ المعاملة',
        selectShopFirst: 'الرجاء اختيار متجر أولاً',
        noShopSelected: 'لم يتم اختيار متجر',
        expenseCategory: 'فئة المصروف',
        expenseName: 'اسم المصروف',
        unpaidInvoices: 'فواتير غير مدفوعة',
        noUnpaidInvoices: 'لا توجد فواتير غير مدفوعة',
        grandTotal: 'المجموع الكلي',
        alreadyPaid: 'المدفوع',
        balanceDue: 'الرصيد المستحق',
        paymentType: 'نوع الدفع',
        fullPayment: 'دفع كامل',
        partialPayment: 'دفع جزئي',
        paymentDetails: 'تفاصيل الدفع',
        notes: 'ملاحظات',
        selectReference: 'اختر المرجع',
        expenseDetails: 'تفاصيل المصروف',
        general: 'عام',
        searchPlaceholder: 'بحث...',
        noResults: 'لا توجد نتائج',
        select: 'اختر',
        invoice: 'فاتورة',
        due: 'مستحق',
        paid: 'مدفوع',
        total: 'المجموع',
        customer: 'عميل',
        supplier: 'مورد',
        employee: 'موظف',
        name: 'الاسم',
        phone: 'الهاتف',
        email: 'البريد الإلكتروني',
        reference: 'المرجع',
        selectInvoice: 'اختر فاتورة',
        paymentAmount: 'مبلغ الدفع',
        totalBalance: 'الرصيد الإجمالي',
        outstandingBalance: 'الرصيد المستحق',
        selectInvoices: 'اختر الفواتير',
        invoiceSelected: 'تم اختيار الفاتورة',
        pay: 'ادفع',
        balance: 'الرصيد',
        totalDue: 'إجمالي المستحق',
        selectAll: 'اختر الكل',
        deselectAll: 'إلغاء اختيار الكل',
        remainingBalance: 'الرصيد المتبقي',
        noSupplierSelected: 'لم يتم اختيار مورد',
        selectSupplierFirst: 'الرجاء اختيار مورد أولاً',
        paymentAmountExceeds: 'مبلغ الدفع يتجاوز إجمالي الفواتير المختارة',
        invoicesSelected: 'فواتير مختارة',
        totalSelected: 'الإجمالي المختار',
        paymentWillBeApplied: 'سيتم تطبيق الدفع على الفواتير المختارة باستخدام FIFO (الأقدم أولاً)',
        supplierBalance: 'رصيد المورد',
        loadingInvoices: 'جاري تحميل الفواتير...',
        fifoNote: 'FIFO: يتم تطبيق الدفع على أقدم الفواتير أولاً',
        totalOutstanding: 'إجمالي المستحق',
        selectInvoicesToPay: 'اختر الفواتير للدفع',
        payment: 'دفع',
        pending: 'قيد الانتظار',
        partiallyPaid: 'مدفوع جزئياً',
        totalPaid: 'إجمالي المدفوع',
        totalPurchases: 'إجمالي المشتريات',
        summary: 'ملخص',
        selectCategory: 'اختر فئة...'
    },
    'es': {
        addTransaction: 'Agregar Transacción',
        addNewTransaction: 'Registrar una nueva transacción financiera',
        customerDue: 'Deuda del Cliente',
        customerPayment: 'Pago del Cliente',
        supplierDue: 'Deuda del Proveedor',
        supplierPayment: 'Pago al Proveedor',
        salaryPayment: 'Pago de Salario',
        shopExpense: 'Gasto de Tienda',
        selectCustomer: 'Seleccionar Cliente',
        selectSupplier: 'Seleccionar Proveedor',
        selectEmployee: 'Seleccionar Empleado',
        amount: 'Monto',
        date: 'Fecha',
        description: 'Descripción',
        paymentMethod: 'Método de Pago',
        cash: 'Efectivo',
        bankTransfer: 'Transferencia Bancaria',
        cheque: 'Cheque',
        creditCard: 'Tarjeta de Crédito',
        mobilePayment: 'Pago Móvil',
        invoiceNumber: 'Número de Factura',
        optional: 'Opcional',
        save: 'Guardar',
        clear: 'Limpiar',
        saving: 'Guardando...',
        success: 'Éxito',
        error: 'Error',
        warning: 'Advertencia',
        transactionSaved: '¡Transacción guardada exitosamente!',
        failedSave: 'Error al guardar la transacción',
        selectShopFirst: 'Seleccione una tienda primero',
        noShopSelected: 'No se seleccionó ninguna tienda',
        expenseCategory: 'Categoría de Gasto',
        expenseName: 'Nombre del Gasto',
        unpaidInvoices: 'Facturas Impagas',
        noUnpaidInvoices: 'No se encontraron facturas impagas',
        grandTotal: 'Gran Total',
        alreadyPaid: 'Ya Pagado',
        balanceDue: 'Saldo Pendiente',
        paymentType: 'Tipo de Pago',
        fullPayment: 'Pago Completo',
        partialPayment: 'Pago Parcial',
        paymentDetails: 'Detalles de Pago',
        notes: 'Notas',
        selectReference: 'Seleccionar Referencia',
        expenseDetails: 'Detalles de Gasto',
        general: 'General',
        searchPlaceholder: 'Buscar...',
        noResults: 'No hay resultados',
        select: 'Seleccionar',
        invoice: 'Factura',
        due: 'Pendiente',
        paid: 'Pagado',
        total: 'Total',
        customer: 'Cliente',
        supplier: 'Proveedor',
        employee: 'Empleado',
        name: 'Nombre',
        phone: 'Teléfono',
        email: 'Correo',
        reference: 'Referencia',
        selectInvoice: 'Seleccionar Factura',
        paymentAmount: 'Monto de Pago',
        totalBalance: 'Saldo Total',
        outstandingBalance: 'Saldo Pendiente',
        selectInvoices: 'Seleccionar Facturas',
        invoiceSelected: 'Factura Seleccionada',
        pay: 'Pagar',
        balance: 'Saldo',
        totalDue: 'Total Pendiente',
        selectAll: 'Seleccionar Todo',
        deselectAll: 'Deseleccionar Todo',
        remainingBalance: 'Saldo Restante',
        noSupplierSelected: 'No se seleccionó proveedor',
        selectSupplierFirst: 'Seleccione un proveedor primero',
        paymentAmountExceeds: 'El monto del pago excede el total de las facturas seleccionadas',
        invoicesSelected: 'facturas seleccionadas',
        totalSelected: 'Total Seleccionado',
        paymentWillBeApplied: 'El pago se aplicará a las facturas seleccionadas usando FIFO (primero las más antiguas)',
        supplierBalance: 'Saldo del Proveedor',
        loadingInvoices: 'Cargando facturas...',
        fifoNote: 'FIFO: Los pagos se aplican primero a las facturas más antiguas',
        totalOutstanding: 'Total Pendiente',
        selectInvoicesToPay: 'Seleccione facturas para pagar',
        payment: 'Pago',
        pending: 'Pendiente',
        partiallyPaid: 'Pagado Parcialmente',
        totalPaid: 'Total Pagado',
        totalPurchases: 'Total Compras',
        summary: 'Resumen',
        selectCategory: 'Seleccionar categoría...'
    }
};

// ==============================================
// CUSTOM EXPENSE CATEGORY DROPDOWN COMPONENT
// ==============================================
const CustomExpenseDropdown = ({
    value,
    onChange,
    disabled,
    placeholder,
    t,
    isRTL
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Get unique groups for grouping
    const groups = [...new Set(EXPENSE_CATEGORIES.map(cat => cat.group))];

    // Filter categories based on search
    const filteredCategories = EXPENSE_CATEGORIES.filter(cat =>
        cat.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cat.group.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cat.value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Find selected category
    const selectedCategory = EXPENSE_CATEGORIES.find(cat => cat.value === value);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle selection
    const handleSelect = (category) => {
        onChange(category.value);
        setIsOpen(false);
        setSearchTerm('');
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Toggle dropdown
    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                setSearchTerm('');
            }
        }
    };

    return (
        <div 
            className="costrans-custom-dropdown-container" 
            ref={dropdownRef}
            style={{ position: 'relative', width: '100%' }}
        >
            {/* Dropdown Trigger */}
            <div 
                className={`costrans-custom-dropdown-trigger ${isOpen ? 'costrans-open' : ''} ${disabled ? 'costrans-disabled' : ''}`}
                onClick={toggleDropdown}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '0.7rem 32px 0.7rem 0.7rem',
                    border: `1px solid var(--costrans-border)`,
                    borderRadius: '0',
                    background: disabled ? 'var(--costrans-surface)' : 'var(--costrans-bg)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    minHeight: '42px',
                    position: 'relative',
                    transition: 'var(--costrans-transition)',
                    opacity: disabled ? 0.6 : 1,
                    boxSizing: 'border-box'
                }}
            >
                {selectedCategory ? (
                    <>
                        <FontAwesomeIcon 
                            icon={selectedCategory.icon} 
                            style={{ 
                                color: 'var(--costrans-primary)', 
                                fontSize: '18px',
                                flexShrink: 0,
                                width: '20px'
                            }} 
                        />
                        <span style={{ 
                            flex: 1, 
                            fontSize: '0.85rem', 
                            color: 'var(--costrans-text)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {selectedCategory.label}
                        </span>
                        <span style={{
                            fontSize: '0.6rem',
                            color: 'var(--costrans-text-light)',
                            background: 'var(--costrans-surface)',
                            padding: '1px 8px',
                            border: '1px solid var(--costrans-border-light)',
                            flexShrink: 0,
                            display: 'none'
                        }}>
                            {selectedCategory.group}
                        </span>
                    </>
                ) : (
                    <span style={{ 
                        color: 'var(--costrans-text-light)', 
                        fontSize: '0.85rem',
                        opacity: 0.7
                    }}>
                        {placeholder || t('selectCategory') || 'Select a category...'}
                    </span>
                )}
                <FontAwesomeIcon 
                    icon={faChevronDown} 
                    style={{
                        position: 'absolute',
                        right: isRTL ? 'auto' : '0.7rem',
                        left: isRTL ? '0.7rem' : 'auto',
                        color: 'var(--costrans-text-light)',
                        fontSize: '0.7rem',
                        transition: 'transform 0.3s ease',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }} 
                />
            </div>

            {/* Dropdown Options */}
            {isOpen && (
                <div 
                    className="costrans-custom-dropdown-options"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: 'var(--costrans-bg)',
                        border: `1px solid var(--costrans-border)`,
                        borderRadius: '0',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                        maxHeight: '360px',
                        overflowY: 'auto',
                        zIndex: 9999,
                        padding: '8px 0',
                        animation: 'costransDropdownSlideIn 0.25s ease'
                    }}
                >
                    {/* Search Input */}
                    <div style={{ padding: '0 12px 8px 12px', position: 'sticky', top: 0, background: 'var(--costrans-bg)', zIndex: 2 }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            border: `1px solid var(--costrans-border-light)`,
                            borderRadius: '0',
                            background: 'var(--costrans-surface)',
                            transition: 'var(--costrans-transition)'
                        }}>
                            <FontAwesomeIcon 
                                icon={faSearch} 
                                style={{ color: 'var(--costrans-text-light)', fontSize: '14px' }} 
                            />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search categories..."
                                style={{
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    color: 'var(--costrans-text)',
                                    fontSize: '0.85rem',
                                    flex: 1,
                                    padding: '4px 0',
                                    fontFamily: 'inherit'
                                }}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSearchTerm('');
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--costrans-text-light)',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        padding: '2px 6px'
                                    }}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Categories List */}
                    {filteredCategories.length === 0 ? (
                        <div style={{
                            padding: '30px 20px',
                            textAlign: 'center',
                            color: 'var(--costrans-text-light)',
                            fontSize: '0.85rem'
                        }}>
                            <FontAwesomeIcon icon={faSearch} style={{ fontSize: '24px', opacity: 0.3, marginBottom: '8px', display: 'block' }} />
                            No categories found
                        </div>
                    ) : (
                        <div>
                            {groups.map(group => {
                                const groupCategories = filteredCategories.filter(cat => cat.group === group);
                                if (groupCategories.length === 0) return null;
                                return (
                                    <div key={group}>
                                        {/* Group Header */}
                                        <div style={{
                                            padding: '6px 16px',
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            color: 'var(--costrans-text-light)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.8px',
                                            borderTop: `1px solid var(--costrans-border-light)`,
                                            marginTop: group !== groups[0] ? '4px' : 0,
                                            paddingTop: group !== groups[0] ? '8px' : '6px',
                                            background: 'var(--costrans-surface)',
                                            position: 'sticky',
                                            top: '52px',
                                            zIndex: 1
                                        }}>
                                            {group}
                                        </div>
                                        {groupCategories.map(category => (
                                            <div
                                                key={category.value}
                                                className={`costrans-custom-dropdown-item ${value === category.value ? 'costrans-selected' : ''}`}
                                                onClick={() => handleSelect(category)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    padding: '8px 16px',
                                                    cursor: 'pointer',
                                                    transition: 'var(--costrans-transition)',
                                                    background: value === category.value ? 'rgba(179,185,16,0.08)' : 'transparent',
                                                    borderLeft: value === category.value ? `3px solid var(--costrans-primary)` : '3px solid transparent',
                                                    borderRadius: '0'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (value !== category.value) {
                                                        e.currentTarget.style.background = 'var(--costrans-surface)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (value !== category.value) {
                                                        e.currentTarget.style.background = 'transparent';
                                                    }
                                                }}
                                            >
                                                <FontAwesomeIcon 
                                                    icon={category.icon} 
                                                    style={{ 
                                                        color: value === category.value ? 'var(--costrans-primary)' : 'var(--costrans-text-light)',
                                                        fontSize: '16px',
                                                        flexShrink: 0,
                                                        width: '20px'
                                                    }} 
                                                />
                                                <span style={{
                                                    flex: 1,
                                                    fontSize: '0.85rem',
                                                    color: value === category.value ? 'var(--costrans-text)' : 'var(--costrans-text)'
                                                }}>
                                                    {category.label}
                                                </span>
                                                {value === category.value && (
                                                    <FontAwesomeIcon 
                                                        icon={faCheck} 
                                                        style={{ 
                                                            color: 'var(--costrans-primary)',
                                                            fontSize: '14px',
                                                            flexShrink: 0
                                                        }} 
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <style>{`
                @keyframes costransDropdownSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-8px) scale(0.98);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                
                .costrans-custom-dropdown-options::-webkit-scrollbar {
                    width: 4px;
                }
                .costrans-custom-dropdown-options::-webkit-scrollbar-track {
                    background: var(--costrans-surface);
                }
                .costrans-custom-dropdown-options::-webkit-scrollbar-thumb {
                    background: var(--costrans-border);
                }
                .costrans-custom-dropdown-options::-webkit-scrollbar-thumb:hover {
                    background: var(--costrans-primary);
                }
                
                .costrans-dark .costrans-custom-dropdown-options {
                    background: var(--costrans-bg);
                    border-color: var(--costrans-border);
                    box-shadow: 0 10px 25px rgba(0,0,0,0.4);
                }
                
                .costrans-dark .costrans-custom-dropdown-trigger {
                    border-color: var(--costrans-border);
                }
                
                .costrans-dark .costrans-custom-dropdown-item {
                    color: var(--costrans-text);
                }
                
                .costrans-dark .costrans-custom-dropdown-item:hover {
                    background: var(--costrans-surface) !important;
                }
                
                .costrans-dark .costrans-custom-dropdown-item.costrans-selected {
                    background: rgba(179,185,16,0.15) !important;
                }
            `}</style>
        </div>
    );
};

// ==============================================
// TOAST NOTIFICATION
// ==============================================
const ToastNotification = ({ toast, setToast, t }) => {
    if (!toast.show) return null;
    return (
        <div className={`costrans_toast-notification ${toast.type}`}>
            <div className="costrans_toast-content">
                <div className="costrans_toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} />
                </div>
                <div className="costrans_toast-text">
                    <div className="costrans_toast-title">{t(toast.type === 'success' ? 'success' : 'error')}</div>
                    <div className="costrans_toast-message">{toast.message}</div>
                </div>
                <button className="costrans_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// SUPPLIER LIST ITEM (with green tick when selected)
// ==============================================
const SupplierItem = ({ supplier, isSelected, onClick, t }) => (
    <div
        className={`costrans-ref-item ${isSelected ? 'costrans-selected' : ''}`}
        onClick={onClick}
    >
        <div className="costrans-ref-info">
            <span className="costrans-ref-name">{supplier.name}</span>
            {supplier.phone && <span className="costrans-ref-detail">{supplier.phone}</span>}
        </div>
        <div className="costrans-ref-due">
            {supplier.unpaid_count > 0 && (
                <span className="costrans-unpaid-count">{supplier.unpaid_count} {t('invoice')}</span>
            )}
        </div>
        {isSelected && (
            <div className="costrans-selected-check">
                <FontAwesomeIcon icon={faCheck} className="costrans-check-icon" />
            </div>
        )}
    </div>
);

// ==============================================
// INVOICE ITEM (with status icons)
// ==============================================
const InvoiceItem = ({ invoice, isSelected, onClick, t }) => {
    const dueAmount = parseFloat(invoice.balance || 0);
    const totalAmount = parseFloat(invoice.amount || 0);
    const paidAmount = totalAmount - dueAmount;
    const isPaid = dueAmount <= 0.01;
    const isPartial = !isPaid && paidAmount > 0.01;

    let statusIcon = faCircle;
    let statusColor = 'var(--costrans-error)';
    let statusText = t('pending');
    let statusClass = 'pending';

    if (isPaid) {
        statusIcon = faCheckDouble;
        statusColor = 'var(--costrans-success)';
        statusText = t('paid');
        statusClass = 'paid';
    } else if (isPartial) {
        statusIcon = faExclamationCircle;
        statusColor = 'var(--costrans-warning)';
        statusText = t('partiallyPaid');
        statusClass = 'partial';
    }

    return (
        <div
            className={`costrans-invoice-item ${isSelected ? 'costrans-selected' : ''}`}
            onClick={onClick}
        >
            <div className="costrans-invoice-checkbox">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
            <div className="costrans-invoice-info">
                <span className="costrans-invoice-number">
                    <FontAwesomeIcon icon={faFileInvoice} /> {invoice.invoice_number || `INV-${invoice.purchase_id}`}
                </span>
                <span className="costrans-invoice-date">
                    {new Date(invoice.transaction_date).toLocaleDateString()}
                </span>
            </div>
            <div className="costrans-invoice-amounts">
                <span className="costrans-invoice-total">
                    Rs. {formatCurrency(totalAmount)}
                </span>
                {!isPaid && (
                    <span className={`costrans-invoice-due ${statusClass}`}>
                        {t('due')}: Rs. {formatCurrency(dueAmount)}
                    </span>
                )}
                {isPaid && (
                    <span className="costrans-invoice-due paid">
                        {t('paid')}: Rs. {formatCurrency(totalAmount)}
                    </span>
                )}
            </div>
            <div className="costrans-invoice-status">
                <FontAwesomeIcon
                    icon={statusIcon}
                    className={`costrans-status-icon ${statusClass}`}
                    style={{ color: statusColor }}
                />
                <span className={`costrans-status-badge ${statusClass}`}>
                    {statusText}
                </span>
            </div>
        </div>
    );
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const AddTransaction = () => {
    const { user, currentShop } = useAuth();

    // ── State ──────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState('supplier_payment');
    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [searchTerm, setSearchTerm] = useState('');

    // ── Supplier Payment specific state ──────────────────────
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [supplierUnpaidInvoices, setSupplierUnpaidInvoices] = useState([]);
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [isFullPayment, setIsFullPayment] = useState(true);
    const [totalBalance, setTotalBalance] = useState(0);
    const [supplierSummary, setSupplierSummary] = useState({
        totalBalance: 0,
        totalPaid: 0,
        totalPurchases: 0
    });
    const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');

    // ── Form Data ─────────────────────────────────────────────
    const [formData, setFormData] = useState({
        reference_id: '',
        amount: '',
        transaction_date: new Date().toISOString().split('T')[0],
        description: '',
        payment_method: 'cash',
        invoice_number: '',
        expense_category: '',
        expense_name: ''
    });

    // ── Language & Theme ──────────────────────────────────────
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });

    // ── Refs ──────────────────────────────────────────────────
    const toastTimerRef = useRef(null);
    const isSubmittingRef = useRef(false);
    const fetchedRefs = useRef(false);
    const leftColumnRef = useRef(null);

    // ── Translation helper ──────────────────────────────────
    const t = useCallback((key) => {
        const lang = currentLanguage in translations ? currentLanguage : 'en';
        return translations[lang][key] || key;
    }, [currentLanguage]);

    // ── Toast ──────────────────────────────────────────────────
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    // ── Tab definitions ──────────────────────────────────────
    const tabs = [
        { id: 'supplier_payment', label: t('supplierPayment'), icon: faBuilding },
        { id: 'customer_due', label: t('customerDue'), icon: faAdjust },
        { id: 'expense', label: t('shopExpense'), icon: faReceipt },
        { id: 'employee_salary', label: t('salaryPayment'), icon: faUserTie },
        { id: 'customer_payment', label: t('customerPayment'), icon: faUser },
        { id: 'supplier_due', label: t('supplierDue'), icon: faUser },
    ];

    const paymentMethodOptions = [
        { value: 'cash', label: t('cash') },
        { value: 'bank_transfer', label: t('bankTransfer') },
        { value: 'cheque', label: t('cheque') },
        { value: 'credit_card', label: t('creditCard') },
        { value: 'mobile_payment', label: t('mobilePayment') }
    ];

    // ── Reset form ────────────────────────────────────────────
    const resetForm = useCallback(() => {
        setFormData({
            reference_id: '',
            amount: '',
            transaction_date: new Date().toISOString().split('T')[0],
            description: '',
            payment_method: 'cash',
            invoice_number: '',
            expense_category: '',
            expense_name: ''
        });
        setSelectedSupplier(null);
        setSelectedInvoices([]);
        setPaymentAmount('');
        setIsFullPayment(true);
        setSupplierUnpaidInvoices([]);
        setTotalBalance(0);
        setSearchTerm('');
        setInvoiceSearchTerm('');
        setSupplierSummary({ totalBalance: 0, totalPaid: 0, totalPurchases: 0 });
    }, []);

    // ── Load suppliers with balances ─────────────────────────
    const loadSuppliersWithBalances = useCallback(async () => {
        if (!currentShop) return;
        try {
            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_all_suppliers',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setSuppliers(data.data || []);
            } else {
                const fallbackRes = await fetch(SUPPLIERS_API, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include'
                });
                const fallbackData = await fallbackRes.json();
                if (fallbackData.status === 'success') {
                    setSuppliers(fallbackData.data || []);
                }
            }
        } catch (err) {
            console.warn('Failed to load suppliers:', err);
            setSuppliers([]);
        }
    }, [currentShop]);

    // ── Load supplier unpaid invoices ────────────────────────
    const loadSupplierUnpaidInvoices = useCallback(async (supplierId) => {
        if (!currentShop || !supplierId) {
            setSupplierUnpaidInvoices([]);
            setTotalBalance(0);
            setSupplierSummary({ totalBalance: 0, totalPaid: 0, totalPurchases: 0 });
            return;
        }
        setLoadingInvoices(true);
        try {
            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_supplier_unpaid_invoices',
                    shop_id: currentShop.shop_id,
                    supplier_id: supplierId
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setSupplierUnpaidInvoices(data.data || []);
                setTotalBalance(data.total_balance || 0);

                setSupplierSummary({
                    totalBalance: data.total_balance || 0,
                    totalPaid: data.total_paid || 0,
                    totalPurchases: data.total_purchases || 0
                });
            } else {
                setSupplierUnpaidInvoices([]);
                setTotalBalance(0);
                setSupplierSummary({ totalBalance: 0, totalPaid: 0, totalPurchases: 0 });
            }
        } catch (err) {
            console.warn('Failed to load supplier unpaid invoices:', err);
            setSupplierUnpaidInvoices([]);
            setTotalBalance(0);
            setSupplierSummary({ totalBalance: 0, totalPaid: 0, totalPurchases: 0 });
        } finally {
            setLoadingInvoices(false);
        }
    }, [currentShop]);

    // ── Load customers ────────────────────────────────────────
    const loadCustomers = useCallback(async () => {
        if (!currentShop) return;
        try {
            const res = await fetch(CUSTOMERS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setCustomers(data.data || []);
            }
        } catch { /* ignore */ }
    }, [currentShop]);

    // ── Load employees ────────────────────────────────────────
    const loadEmployees = useCallback(async () => {
        if (!currentShop) return;
        try {
            const res = await fetch(EMPLOYEES_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setEmployees(data.data || []);
            }
        } catch { /* ignore */ }
    }, [currentShop]);

    // ── Load all data ────────────────────────────────────────
    useEffect(() => {
        if (currentShop && !fetchedRefs.current) {
            fetchedRefs.current = true;
            loadSuppliersWithBalances();
            loadCustomers();
            loadEmployees();
        }
    }, [currentShop, loadSuppliersWithBalances, loadCustomers, loadEmployees]);

    // ── Reset form when tab changes ──────────────────────────
    useEffect(() => {
        resetForm();
    }, [activeTab, resetForm]);

    // ── Handle tab change ────────────────────────────────────
    const handleTabChange = useCallback((tabId) => {
        setActiveTab(tabId);
    }, []);

    // ── Handle supplier selection ────────────────────────────
    const handleSupplierSelect = useCallback((supplier) => {
        setSelectedSupplier(supplier);
        setSelectedInvoices([]);
        setPaymentAmount('');
        setFormData(prev => ({ ...prev, reference_id: supplier.id }));
        if (activeTab === 'supplier_payment' && supplier.id) {
            loadSupplierUnpaidInvoices(supplier.id);
        }
    }, [activeTab, loadSupplierUnpaidInvoices]);

    // ── Toggle invoice selection ─────────────────────────────
    const toggleInvoiceSelection = useCallback((invoice) => {
        setSelectedInvoices(prev => {
            const exists = prev.find(inv => inv.purchase_id === invoice.purchase_id);
            if (exists) {
                return prev.filter(inv => inv.purchase_id !== invoice.purchase_id);
            } else {
                return [...prev, invoice];
            }
        });
    }, []);

    // ── Calculate total selected balance ─────────────────────
    const getSelectedTotal = useCallback(() => {
        return selectedInvoices.reduce((sum, inv) => sum + parseFloat(inv.balance || 0), 0);
    }, [selectedInvoices]);

    // ── Handle payment amount change ──────────────────────────
    const handlePaymentAmountChange = useCallback((value) => {
        setPaymentAmount(value);
        setFormData(prev => ({ ...prev, amount: value }));

        const selectedTotal = getSelectedTotal();
        const enteredAmount = parseFloat(value) || 0;
        setIsFullPayment(enteredAmount >= selectedTotal - 0.01);
    }, [getSelectedTotal]);

    // ── Get filtered suppliers ───────────────────────────────
    const getFilteredSuppliers = useCallback(() => {
        let list = suppliers;
        const search = searchTerm.toLowerCase().trim();

        if (search) {
            list = list.filter(s =>
                (s.name || '').toLowerCase().includes(search) ||
                (s.supplier_name || '').toLowerCase().includes(search) ||
                (s.phone || '').includes(search)
            );
        }

        return list.sort((a, b) => (parseFloat(b.balance || 0) - parseFloat(a.balance || 0)));
    }, [suppliers, searchTerm]);

    // ── Get filtered invoices ────────────────────────────────
    const getFilteredInvoices = useCallback(() => {
        let list = supplierUnpaidInvoices;
        const search = invoiceSearchTerm.toLowerCase().trim();

        if (search) {
            list = list.filter(inv =>
                (inv.invoice_number || '').toLowerCase().includes(search) ||
                (inv.purchase_id || '').toString().includes(search)
            );
        }

        return list.sort((a, b) => {
            const aBalance = parseFloat(a.balance || 0);
            const bBalance = parseFloat(b.balance || 0);
            const aPaid = aBalance <= 0.01;
            const bPaid = bBalance <= 0.01;
            const aPartial = !aPaid && parseFloat(a.amount || 0) - aBalance > 0.01;
            const bPartial = !bPaid && parseFloat(b.amount || 0) - bBalance > 0.01;

            if (!aPaid && bPaid) return -1;
            if (aPaid && !bPaid) return 1;

            if (aPartial && !bPartial && !bPaid) return -1;
            if (!aPartial && bPartial && !aPaid) return 1;

            return 0;
        });
    }, [supplierUnpaidInvoices, invoiceSearchTerm]);

    // ── Select all invoices ──────────────────────────────────
    const selectAllInvoices = useCallback(() => {
        const filtered = getFilteredInvoices();
        if (selectedInvoices.length === filtered.length && filtered.length > 0) {
            setSelectedInvoices([]);
        } else {
            setSelectedInvoices([...filtered]);
        }
    }, [selectedInvoices, getFilteredInvoices]);

    // ── Get reference label ──────────────────────────────────
    const getReferenceLabel = useCallback(() => {
        switch (activeTab) {
            case 'customer_payment':
                return t('customer');
            case 'supplier_payment':
                return t('supplier');
            case 'employee_salary':
                return t('employee');
            default:
                return t('reference');
        }
    }, [activeTab, t]);

    // ── Add transaction ───────────────────────────────────────
    const handleAddTransaction = useCallback(async () => {
        if (isSubmittingRef.current) return;
        if (!currentShop) {
            showToast('error', t('selectShopFirst'));
            return;
        }

        // Validation for Supplier Payment
        if (activeTab === 'supplier_payment') {
            if (!selectedSupplier) {
                showToast('error', t('selectSupplierFirst'));
                return;
            }
            if (selectedInvoices.length === 0) {
                showToast('error', t('selectInvoices') + ' required');
                return;
            }
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', t('paymentAmount') + ' required');
                return;
            }

            const selectedTotal = getSelectedTotal();
            if (parseFloat(formData.amount) > selectedTotal + 0.01) {
                showToast('error', t('paymentAmountExceeds'));
                return;
            }
        } else if (activeTab === 'expense') {
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', t('amount') + ' required');
                return;
            }
            // Expense name is now optional when category is selected
            if (!formData.expense_category && !formData.expense_name) {
                showToast('error', t('expenseCategory') + ' or ' + t('expenseName') + ' required');
                return;
            }
        } else {
            if (!selectedSupplier) {
                showToast('error', t('select') + ' ' + getReferenceLabel());
                return;
            }
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', t('amount') + ' required');
                return;
            }
        }

        isSubmittingRef.current = true;
        setLoading(true);

        try {
            const actionMap = {
                customer_due: 'add_due',
                customer_payment: 'add_payment',
                supplier_due: 'add_supplier_due',
                supplier_payment: 'add_supplier_payment',
                employee_salary: 'add_employee_salary',
                expense: 'add_expense'
            };
            const refKeyMap = {
                customer_due: 'customer_id',
                customer_payment: 'customer_id',
                supplier_due: 'supplier_id',
                supplier_payment: 'supplier_id',
                employee_salary: 'employee_id'
            };
            const action = actionMap[activeTab];
            if (!action) throw new Error('Invalid transaction type');

            const body = {
                action,
                shop_id: currentShop.shop_id,
                username: user?.username || 'system',
                transaction_date: formData.transaction_date,
                payment_method: formData.payment_method,
                description: formData.description,
                amount: parseFloat(formData.amount)
            };

            if (activeTab === 'supplier_payment') {
                body.supplier_id = parseInt(selectedSupplier.id);
                body.invoice_ids = selectedInvoices.map(inv => inv.purchase_id);
                if (formData.invoice_number) {
                    body.invoice_number = formData.invoice_number;
                }
            } else if (refKeyMap[activeTab]) {
                body[refKeyMap[activeTab]] = selectedSupplier.id;
            }

            if (activeTab === 'expense') {
                // If category is selected but name is empty, use category label as name
                if (formData.expense_category && !formData.expense_name) {
                    const category = EXPENSE_CATEGORIES.find(cat => cat.value === formData.expense_category);
                    body.expense_name = category ? category.label : formData.expense_category;
                } else {
                    body.expense_name = formData.expense_name;
                }
                body.expense_category = formData.expense_category || 'General';
            }

            if (activeTab === 'supplier_due') {
                body.invoice_number = formData.invoice_number || `SUP-DUE-${Date.now()}`;
            }

            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();

            if (data.status === 'success') {
                showToast('success', t('transactionSaved'));
                resetForm();
                loadSuppliersWithBalances();
                setSupplierUnpaidInvoices([]);
                setSelectedInvoices([]);
                setTotalBalance(0);
                setSupplierSummary({ totalBalance: 0, totalPaid: 0, totalPurchases: 0 });
            } else {
                showToast('error', data.message || t('failedSave'));
            }
        } catch (err) {
            showToast('error', err.message || t('failedSave'));
        } finally {
            setLoading(false);
            setTimeout(() => {
                isSubmittingRef.current = false;
            }, 500);
        }
    }, [currentShop, formData, activeTab, user, showToast, resetForm,
        selectedSupplier, selectedInvoices, loadSuppliersWithBalances, t, getSelectedTotal, getReferenceLabel]);

    // ── Cleanup ───────────────────────────────────────────────
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // ── Theme and Dark Mode ──────────────────────────────────
    useEffect(() => {
        const applyTheme = (color) => {
            if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
                color = '#b3b910';
            }
            document.documentElement.style.setProperty('--costrans-primary', color);
            document.documentElement.style.setProperty('--costrans-primary-dark', color);
            document.documentElement.style.setProperty('--costrans-primary-light', color);
        };

        const savedTheme = localStorage.getItem('ourzest_theme') || '#b3b910';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyTheme(e.newValue || '#b3b910');
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            const container = document.querySelector('.costrans-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('costrans-dark');
                } else {
                    container.classList.remove('costrans-dark');
                }
            }
        };

        syncDarkMode();
        window.addEventListener('storage', (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        });
        return () => {
            window.removeEventListener('storage', syncDarkMode);
        };
    }, []);

    // ── RTL ──────────────────────────────────────────────────
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    useEffect(() => {
        const container = document.querySelector('.costrans-container');
        if (container) {
            if (isRTL) {
                container.classList.add('costrans-rtl');
                container.classList.remove('costrans-ltr');
            } else {
                container.classList.add('costrans-ltr');
                container.classList.remove('costrans-rtl');
            }
        }
    }, [isRTL]);

    // ── Render ─────────────────────────────────────────────────
    const filteredSuppliers = getFilteredSuppliers();
    const filteredInvoices = getFilteredInvoices();

    const isSupplierPayment = activeTab === 'supplier_payment';
    const isSupplierDue = activeTab === 'supplier_due';
    const isExpense = activeTab === 'expense';
    const isCustomerTab = activeTab === 'customer_due' || activeTab === 'customer_payment';
    const isEmployeeTab = activeTab === 'employee_salary';
    const selectedTotal = getSelectedTotal();
    const remainingBalance = selectedTotal - (parseFloat(paymentAmount || formData.amount) || 0);

    return (
        <div className={`costrans-container ${isRTL ? 'costrans-rtl' : 'costrans-ltr'} ${darkMode ? 'costrans-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} t={t} />

            {/* Two Column Layout */}
            <div className="costrans-two-column-layout">
                {/* Left Column - Static scrolling only when mouse is inside */}
                <div
                    className="costrans-left-column"
                    ref={leftColumnRef}
                    onMouseEnter={() => {
                        if (leftColumnRef.current) {
                            leftColumnRef.current.style.overflowY = 'auto';
                        }
                    }}
                    onMouseLeave={() => {
                        if (leftColumnRef.current) {
                            leftColumnRef.current.style.overflowY = 'hidden';
                        }
                    }}
                >
                    {/* Upper Section - Tabs with proper layout */}
                    <div className="costrans-tabs-section">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`costrans-tab-btn ${activeTab === tab.id ? 'costrans-active' : ''}`}
                                onClick={() => handleTabChange(tab.id)}
                            >
                                <FontAwesomeIcon icon={tab.icon} />
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* Lower Section - Reference List with search */}
                    <div className="costrans-ref-list-section">
                        <div className="costrans-ref-search">
                            <FontAwesomeIcon icon={faSearch} className="costrans-search-icon" />
                            <input
                                type="text"
                                placeholder={t('searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="costrans-search-input"
                            />
                        </div>
                        <div className="costrans-ref-list">
                            {(activeTab === 'supplier_payment') ? (
                                filteredSuppliers.length === 0 ? (
                                    <div className="costrans-no-results">
                                        <FontAwesomeIcon icon={faList} />
                                        <span>{t('noResults')}</span>
                                    </div>
                                ) : (
                                    filteredSuppliers.map(supplier => (
                                        <SupplierItem
                                            key={supplier.id}
                                            supplier={supplier}
                                            isSelected={selectedSupplier?.id === supplier.id}
                                            onClick={() => handleSupplierSelect(supplier)}
                                            t={t}
                                        />
                                    ))
                                )
                            ) : (activeTab === 'customer_payment') ? (
                                customers.length === 0 ? (
                                    <div className="costrans-no-results">
                                        <FontAwesomeIcon icon={faList} />
                                        <span>{t('noResults')}</span>
                                    </div>
                                ) : (
                                    customers.map(customer => (
                                        <div
                                            key={customer.id || customer.customerid}
                                            className={`costrans-ref-item ${selectedSupplier?.id === (customer.id || customer.customerid) ? 'costrans-selected' : ''}`}
                                            onClick={() => handleSupplierSelect({ id: customer.id || customer.customerid, name: customer.customer_name || customer.name, phone: customer.phone })}
                                        >
                                            <div className="costrans-ref-info">
                                                <span className="costrans-ref-name">{customer.customer_name || customer.name}</span>
                                                {customer.phone && <span className="costrans-ref-detail">{customer.phone}</span>}
                                            </div>
                                            {selectedSupplier?.id === (customer.id || customer.customerid) && (
                                                <div className="costrans-selected-check">
                                                    <FontAwesomeIcon icon={faCheck} className="costrans-check-icon" />
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )
                            ) : (activeTab === 'employee_salary') ? (
                                employees.length === 0 ? (
                                    <div className="costrans-no-results">
                                        <FontAwesomeIcon icon={faList} />
                                        <span>{t('noResults')}</span>
                                    </div>
                                ) : (
                                    employees.map(employee => (
                                        <div
                                            key={employee.id || employee.employee_id}
                                            className={`costrans-ref-item ${selectedSupplier?.id === (employee.id || employee.employee_id) ? 'costrans-selected' : ''}`}
                                            onClick={() => handleSupplierSelect({ id: employee.id || employee.employee_id, name: employee.employee_name || employee.name, phone: employee.phone })}
                                        >
                                            <div className="costrans-ref-info">
                                                <span className="costrans-ref-name">{employee.employee_name || employee.name}</span>
                                                {employee.phone && <span className="costrans-ref-detail">{employee.phone}</span>}
                                            </div>
                                            {selectedSupplier?.id === (employee.id || employee.employee_id) && (
                                                <div className="costrans-selected-check">
                                                    <FontAwesomeIcon icon={faCheck} className="costrans-check-icon" />
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )
                            ) : (
                                <div className="costrans-select-prompt">
                                    <FontAwesomeIcon icon={faArrowLeft} className="costrans-prompt-icon" />
                                    <span>{t('selectReference')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column - Fully scrollable */}
                <div className="costrans-right-column">
                    {/* Upper Section - Invoice Section with Summary */}
                    <div className="costrans-invoice-section">
                        {isSupplierPayment && selectedSupplier ? (
                            <>
                                {/* Summary Header */}
                                <div className="costrans-section-header">
                                    <div className="costrans-header-left-section">
                                        <span className="costrans-badge">{supplierUnpaidInvoices.length}</span>
                                        <span>{t('unpaidInvoices')}</span>
                                        <span className="costrans-summary-value balance">Rs. {formatCurrency(supplierSummary.totalBalance)}</span>
                                    </div>
                                    <div className="costrans-header-right-section">
                                        <button
                                            className="costrans-select-all-btn"
                                            onClick={selectAllInvoices}
                                        >
                                            <FontAwesomeIcon icon={faCheckDouble} />
                                            {selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0 ? t('deselectAll') : t('selectAll')}
                                        </button>
                                    </div>
                                </div>

                                {/* Invoice Search */}
                                <div className="costrans-invoice-search">
                                    <input
                                        type="text"
                                        placeholder="Search invoices..."
                                        value={invoiceSearchTerm}
                                        onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                                        className="costrans-search-input"
                                    />
                                </div>

                                {/* Invoice List */}
                                <div className="costrans-invoice-list">
                                    {loadingInvoices ? (
                                        <div className="costrans-loading-invoices">
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>{t('loadingInvoices')}</span>
                                        </div>
                                    ) : filteredInvoices.length === 0 ? (
                                        <div className="costrans-no-data">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{t('noUnpaidInvoices')}</span>
                                        </div>
                                    ) : (
                                        filteredInvoices.map(inv => (
                                            <InvoiceItem
                                                key={inv.purchase_id}
                                                invoice={inv}
                                                isSelected={selectedInvoices.some(i => i.purchase_id === inv.purchase_id)}
                                                onClick={() => toggleInvoiceSelection(inv)}
                                                t={t}
                                            />
                                        ))
                                    )}
                                </div>
                            </>
                        ) : isCustomerTab || isSupplierDue || isEmployeeTab ? (
                            <div className="costrans-selected-info">
                                {selectedSupplier ? (
                                    <div className="costrans-info-card">
                                        <div className="costrans-info-header">
                                            <FontAwesomeIcon icon={faUser} className="costrans-info-icon" />
                                            <span className="costrans-info-name">{selectedSupplier.name || selectedSupplier.supplier_name}</span>
                                        </div>
                                        {selectedSupplier.phone && (
                                            <div className="costrans-info-row">
                                                <span className="costrans-info-label">{t('phone')}:</span>
                                                <span>{selectedSupplier.phone}</span>
                                            </div>
                                        )}
                                        {selectedSupplier.balance !== undefined && (
                                            <div className="costrans-info-row costrans-due-row">
                                                <span className="costrans-info-label">{t('balance')}:</span>
                                                <span className={`costrans-due-amount ${parseFloat(selectedSupplier.balance) > 0 ? 'costrans-has-due' : 'costrans-no-due'}`}>
                                                    Rs. {formatCurrency(selectedSupplier.balance || 0)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="costrans-select-prompt">
                                        <FontAwesomeIcon icon={faArrowLeft} className="costrans-prompt-icon" />
                                        <span>{t('selectSupplierFirst')}</span>
                                    </div>
                                )}
                            </div>
                        ) : isExpense ? (
                            <div className="costrans-expense-details">
                                <div className="costrans-section-header">
                                    <FontAwesomeIcon icon={faReceipt} />
                                    <span>{t('expenseDetails')}</span>
                                </div>
                                <div className="costrans-expense-fields">
                                    {/* Expense Category - Custom Dropdown */}
                                    <div className="costrans-field-group">
                                        <CustomExpenseDropdown
                                            value={formData.expense_category}
                                            onChange={(value) => {
                                                setFormData(prev => ({ 
                                                    ...prev, 
                                                    expense_category: value,
                                                    // Auto-fill expense name if empty and category is selected
                                                    expense_name: prev.expense_name || (value ? EXPENSE_CATEGORIES.find(cat => cat.value === value)?.label || '' : '')
                                                }));
                                            }}
                                            disabled={loading}
                                            placeholder={t('expenseCategory')}
                                            t={t}
                                            isRTL={isRTL}
                                        />
                                    </div>
                                    {/* Expense Name - Now Optional when category is selected */}
                                    <div className="costrans-field-group">
                                        <input
                                            type="text"
                                            placeholder=" "
                                            value={formData.expense_name}
                                            onChange={e => setFormData(prev => ({ ...prev, expense_name: e.target.value }))}
                                            disabled={loading}
                                            className="costrans-input"
                                            style={{
                                                borderColor: formData.expense_category ? 'var(--costrans-success)' : 'var(--costrans-border)'
                                            }}
                                        />
                                        <label>
                                            {t('expenseName')} 
                                            {!formData.expense_category && <span style={{ color: 'var(--costrans-error)' }}> *</span>}
                                            {formData.expense_category && <span className="costrans-optional" style={{ fontSize: '0.65rem', color: 'var(--costrans-text-light)' }}> (optional)</span>}
                                        </label>
                                        {formData.expense_category && (
                                            <span style={{
                                                position: 'absolute',
                                                right: isRTL ? 'auto' : '8px',
                                                left: isRTL ? '8px' : 'auto',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                fontSize: '0.65rem',
                                                color: 'var(--costrans-success)',
                                                fontWeight: 500
                                            }}>
                                                ✓ Auto-filled
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Lower Section - Payment Details */}
                    <div className="costrans-payment-section">
                        <div className="costrans-section-header">
                            <FontAwesomeIcon icon={faWallet} />
                            <span>{t('paymentDetails')}</span>
                        </div>

                        <div className="costrans-payment-grid">
                            <div className="costrans-field-group costrans-amount-field">
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder=" "
                                    className="costrans-amount-input"
                                    value={paymentAmount || formData.amount}
                                    onChange={e => {
                                        const val = e.target.value;
                                        setPaymentAmount(val);
                                        setFormData(prev => ({ ...prev, amount: val }));
                                        handlePaymentAmountChange(val);
                                    }}
                                    disabled={loading || (isSupplierPayment && selectedInvoices.length === 0)}
                                />
                                <label>{t('amount')}</label>
                            </div>

                            <div className="costrans-field-group">
                                <input
                                    type="date"
                                    placeholder=" "
                                    value={formData.transaction_date}
                                    onChange={e => setFormData(prev => ({ ...prev, transaction_date: e.target.value }))}
                                    disabled={loading}
                                />
                                <label><FontAwesomeIcon icon={faCalendarAlt} />{t('date')}</label>
                            </div>

                            <div className="costrans-field-group costrans-select-group">
                                <select
                                    value={formData.payment_method}
                                    onChange={e => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                                    disabled={loading}
                                    className="costrans-select"
                                >
                                    {paymentMethodOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                <label><FontAwesomeIcon icon={faCreditCard} />{t('paymentMethod')}</label>
                                <FontAwesomeIcon icon={faChevronDown} className="costrans-select-arrow" />
                            </div>

                            <div className="costrans-field-group">
                                <input
                                    type="text"
                                    placeholder=" "
                                    value={formData.invoice_number}
                                    onChange={e => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                                    disabled={loading}
                                />
                                <label><FontAwesomeIcon icon={faFileInvoice} />{t('invoiceNumber')} <span className="costrans-optional">({t('optional')})</span></label>
                            </div>
                        </div>

                        {/* Payment Preview */}
                        {isSupplierPayment && selectedInvoices.length > 0 && (
                            <div className="costrans-payment-preview">
                                <div className="costrans-preview-row">
                                    <span>{t('totalDue')}:</span>
                                    <span>Rs. {formatCurrency(selectedTotal)}</span>
                                </div>
                                <div className="costrans-preview-row costrans-highlight">
                                    <span>{t('paymentAmount')}:</span>
                                    <span className="costrans-payment-amount">Rs. {formatCurrency(parseFloat(paymentAmount || formData.amount) || 0)}</span>
                                </div>
                                <div className="costrans-preview-row">
                                    <span>{t('paymentType')}:</span>
                                    <span className={isFullPayment ? 'costrans-full' : 'costrans-partial'}>
                                        {isFullPayment ? t('fullPayment') : t('partialPayment')}
                                    </span>
                                </div>
                                <div className="costrans-preview-row">
                                    <span>{t('remainingBalance')}:</span>
                                    <span className={remainingBalance > 0.01 ? 'costrans-due-amount' : 'costrans-no-due'}>
                                        Rs. {formatCurrency(Math.max(0, remainingBalance))}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        <div className="costrans-field-group costrans-textarea-group">
                            <textarea
                                rows="2"
                                placeholder=" "
                                value={formData.description}
                                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                disabled={loading}
                            />
                            <label><FontAwesomeIcon icon={faStickyNote} />{t('notes')}</label>
                        </div>

                        {/* Action Buttons */}
                        <div className="costrans-form-actions">
                            <button
                                type="button"
                                className="costrans-btn costrans-btn-secondary"
                                onClick={resetForm}
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span>{t('clear')}</span>
                            </button>

                            <button
                                type="submit"
                                className="costrans-btn costrans-btn-primary"
                                onClick={handleAddTransaction}
                                disabled={loading || !currentShop || (isSupplierPayment && selectedInvoices.length === 0)}
                            >
                                {loading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        <span>{t('saving')}</span>
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} />
                                        <span>{t('save')}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddTransaction;